import React, {
  useCallback,
  Children,
  cloneElement,
  useEffect,
  useRef,
  useState,
  createContext,
  useContext,
  useMemo,
} from 'react';
import { useInView } from 'react-intersection-observer';
import { animated, useSpring, useTrail } from 'react-spring';
import { useMedia } from '../hooks/useMedia';

const threshold = 0;
const triggerOnce = true;

export const inViewConfig = {
  threshold,
  triggerOnce,
  rootMargin: '0px 0px -100px 0px',
};

export const RevealContext = createContext({
  enabled: true,
});

export const getConfig = (inView: boolean, isPrint: boolean, config?: any) => ({
  opacity: inView || isPrint ? 1 : 0,
  y: inView || isPrint ? 0 : 50,
  from: { opacity: isPrint ? 1 : 0, y: isPrint ? 0 : 50 },
  delay: config?.delay ?? 150,
  config: {
    tension: 100,
    ...config,
  },
});

export function useReveal(config?: any) {
  const { enabled } = useContext(RevealContext);
  const [aboveFold, setAboveFold] = useState(false);
  const [inViewRef, inView] = useInView(inViewConfig);
  const isPrint = useMedia('print');
  const style = useSpring(
    getConfig(enabled === true && (inView || aboveFold), isPrint, config),
  );

  const ref = useRef<HTMLElement>();
  const setRefs = useCallback(
    (node) => {
      if (!ref.current) {
        ref.current = node;
        inViewRef(node);
      }
    },
    [inViewRef, ref],
  );

  useEffect(() => {
    if (ref.current) {
      const { y } = ref.current.getBoundingClientRect();
      if (y <= window.innerHeight) {
        setAboveFold(true);
      }
    }
  }, [ref]);

  return { ref: setRefs, style };
}

export function useRevealTrail(children: React.ReactNode, config: any = {}) {
  const ref = useRef<HTMLDivElement>();
  const isPrint = useMedia('print');
  const { enabled } = useContext(RevealContext);
  const [aboveFold, setAboveFold] = useState(false);
  const childs: any = Children.toArray(children);
  const [inViewRef, inView] = useInView(inViewConfig);

  const trail = useTrail(childs.length, {
    ...getConfig(enabled === true && (inView || aboveFold), isPrint),
    ...config,
  });
  const items = useMemo(
    () =>
      trail.map((props, index) => {
        return (
          <animated.div key={index} style={props as any}>
            {childs[index]}
          </animated.div>
        );
      }),
    [childs, trail],
  );

  const setRefs = useCallback(
    (node) => {
      ref.current = node;
      inViewRef(node);
    },
    [inViewRef],
  );

  useEffect(() => {
    const y = ref.current?.getBoundingClientRect().y;
    if (y !== undefined && y <= window.innerHeight) {
      setAboveFold(true);
    }
  }, [ref]);

  const itemsWithStyleProp = useMemo(
    () =>
      trail.map((style, index) => {
        return cloneElement(childs[index], {
          ...childs[index].props,
          style,
        });
      }),
    [childs, trail],
  );

  return { ref: setRefs, items, itemsWithStyleProp, trail };
}
